import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from '../../assets/logo1.png';
import LogoDark from '../../assets/logo2.png';
import { forceLogout } from '../../utils/sessionManager';
import { LogOut } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const Navbar = ({ sidebarExtra = null, hideShortcuts = false }) => {
  const sessionId = localStorage.getItem('authToken');
  const role = localStorage.getItem('role');
  const name = localStorage.getItem('name');
  const displayName = name && name.trim().length > 0 ? name.trim() : null;
  const location = useLocation();

  const { theme } = useTheme();
  const isLight = theme === 'light';

  const logoSrc = isLight ? Logo : LogoDark;

  const dispatchShortcut = (key, code = key) => {
    window.dispatchEvent(
      new KeyboardEvent('keydown', {
        key,
        code,
        bubbles: true,
      })
    );
  };

  // If a valid session token already exists (e.g., user logged in another tab),
  // open the dashboard in a new tab so they can continue without re‑auth.
  useEffect(() => {
    if (sessionId && location.pathname === '/login') {
      const dashboardUrl = '/dashboard';
      // Avoid opening multiple tabs if hook re-runs
      const alreadyOpened = sessionStorage.getItem('dashboard_tab_opened');
      if (!alreadyOpened) {
        window.open(dashboardUrl, '_blank', 'noopener,noreferrer');
        sessionStorage.setItem('dashboard_tab_opened', 'true');
      }
    }
  }, [sessionId, location.pathname]);

  return (
    <>
      <header className="fixed top-0 left-0 w-full z-50 border-b border-glass-border bg-panel/95 backdrop-blur">
        <nav
          className="mx-auto flex w-full max-w-10xl flex-col items-stretch justify-between gap-3 px-3 py-3 sm:px-6 lg:flex-row lg:items-center lg:gap-6"
          aria-label="Main Navigation"
        >
          {/* Logo */}
          <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-start">
            <div className="flex items-center gap-3">
              <img src={logoSrc} alt="Your Journey Your Way logo" className="h-10 w-auto sm:h-12" />
            </div>
          </div>

          {/* Actions */}
          {sessionId && (
            <div className="flex w-full items-center justify-start lg:w-auto lg:justify-end">
              <div className="flex w-full flex-col items-stretch gap-3 rounded-[1.75rem] border border-glass-border bg-bg/70 px-3 py-2 shadow-sm sm:flex-row sm:flex-wrap sm:items-center sm:gap-4 sm:rounded-full lg:w-auto">
                {displayName && (
                  <div
                    className="hidden items-center gap-3 border-glass-border pr-4 sm:flex sm:border-r-2"
                    aria-label={`Signed in as ${displayName}`}
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent px-2 py-2 text-sm font-bold text-[color:var(--accent-foreground)] transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-accent">
                      {displayName
                        .split(' ')
                        .map((name) => name[0])
                        .slice(0, 2)
                        .join('')
                        .toUpperCase()}
                    </span>
                    <div className="leading-tight">
                      <p className="text-[11px] text-text-muted">Signed in as</p>
                      <p className="text-sm font-semibold text-text-light">{displayName}</p>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={forceLogout}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-accent px-3 py-2 text-sm font-bold text-[color:var(--accent-foreground)] transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-accent sm:w-auto"
                  aria-label="Logout"
                >
                  <LogOut size={18} aria-hidden="true" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </nav>
      </header>
      <div className="h-[126px] sm:h-[96px] lg:h-[88px]" aria-hidden="true" />

      {sessionId && role !== 'admin' && !hideShortcuts && (
        <aside
          className="shortcut-panel absolute right-4 top-[6.5rem] z-[40] hidden xl:block xl:w-[34rem] 2xl:w-[38rem] rounded-[2rem] border border-glass-border bg-panel/95 p-4 text-text-light shadow-lg backdrop-blur 2xl:right-6"
          aria-label="Keyboard shortcuts and accessibility preferences"
        >
          <div className={`grid gap-6 ${sidebarExtra ? 'grid-cols-[minmax(0,1fr)_minmax(0,0.92fr)]' : 'grid-cols-1'}`}>
            <section>
              <div className="mb-5">
                <h2 className="text-[24px] font-bold tracking-wide text-text-light">Shortcut Keys</h2>
                <p className="mt-3 text-base leading-relaxed text-text-muted">
                  Use Tab to move through these controls. Press Enter or Space to activate one.
                </p>
              </div>

              <div className="space-y-4 text-base">
                {[
                  {
                    key: 'Space',
                    desc: 'Main control: start, pause, or resume the conversation',
                    onPress: () => dispatchShortcut(' ', 'Space'),
                  },
                  {
                    key: 'M',
                    desc: "Mute or unmute Ruby's voice",
                    onPress: () => dispatchShortcut('m', 'KeyM'),
                  },
                  {
                    key: 'Esc',
                    desc: 'Stop the current conversation completely',
                    onPress: () => dispatchShortcut('Escape', 'Escape'),
                  },
                  {
                    key: 'I',
                    desc: 'Switch between light and dark mode',
                    onPress: () => dispatchShortcut('i', 'KeyI'),
                  },
                ].map(({ key, desc, onPress }) => (
                  <button
                    type="button"
                    key={key}
                    onClick={onPress}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        onPress();
                      }
                    }}
                    className="shortcut-button flex w-full items-center gap-4 rounded-2xl border border-glass-border bg-bg px-2 py-2 text-left transition hover:border-accent hover:bg-muted focus:outline-none focus:ring-2 focus:ring-accent"
                    aria-label={`${key}. ${desc}`}
                  >
                    <span
                      className={`shortcut-key rounded-full bg-accent text-[color:var(--accent-foreground)] font-bold grid place-items-center ${
                        key === 'Space' ? 'min-w-[7.75rem] px-2 h-14 text-[16px]' : 'w-14 h-14 text-[16px]'
                      }`}
                    >
                      {key}
                    </span>
                    <span className="flex-1 text-text-light leading-snug text-left text-[16px]">{desc}</span>
                  </button>
                ))}
              </div>
            </section>

            {sidebarExtra && (
              <section className="border-l border-glass-border pl-6">
                {sidebarExtra}
              </section>
            )}
          </div>
        </aside>
      )}
    </>
  );
};

export default Navbar;
